export type TriStateAnswer = 'yes' | 'no' | 'unsure'

export type ActivityObservation =
  | 'write_create'
  | 'find_information'
  | 'read_documents'
  | 'analyse_predict_recommend'
  | 'process_actions'
  | 'media_understanding'
  | 'control_physical'
  | 'other_unsure'

export type MediaObservation = 'images_video' | 'speech_audio' | 'both' | 'unsure'
export type AnalysisPurposeObservation =
  | 'predict_forecast'
  | 'classify_score'
  | 'recommend_support_decision'
  | 'select_make_decision'
  | 'detect_monitor'
  | 'personalise_experience'
  | 'optimise_plan'
  | 'unsure'
export type HumanCheckObservation = 'every' | 'some' | 'none' | 'unsure'

export type ContributorObservations = {
  activities: ActivityObservation[]
  analysisPurpose?: AnalysisPurposeObservation
  mediaType?: MediaObservation
  actionWithoutApproval?: TriStateAnswer
  humanCheck?: HumanCheckObservation
  userGroups: string[]
  affectedPeople?: string[]
  informationHandling?: TriStateAnswer
  informationTypes?: string[]
  importantDecision?: TriStateAnswer
  seriousImpact?: TriStateAnswer
  regulatedArea?: TriStateAnswer
  vulnerablePeople?: TriStateAnswer
  contestability?: TriStateAnswer
  otherActivityText?: string
}

export type ScreeningSource = 'contributor' | 'derived' | 'product_inherited' | 'specialist_confirmation'

export type ScreeningProvenance = Record<string, {
  source: ScreeningSource
  sourceField: string
  rationale: string
}>

const externalUserGroups = new Set([
  'customers_clients',
  'applicants',
  'students',
  'patients_care_recipients',
  'members_public',
])

const protectedContextGroups = new Set([
  'applicants',
  'students',
  'patients_care_recipients',
  'members_public',
])

const activityMap: Record<ActivityObservation, string[]> = {
  write_create: ['create_transform_content'],
  find_information: ['find_answer_information'],
  read_documents: ['read_process_documents'],
  analyse_predict_recommend: [],
  process_actions: ['automate_process'],
  media_understanding: [],
  control_physical: ['control_physical_system'],
  other_unsure: ['other_needs_confirmation'],
}

const activityOrder: ActivityObservation[] = [
  'write_create',
  'find_information',
  'read_documents',
  'analyse_predict_recommend',
  'process_actions',
  'media_understanding',
  'control_physical',
  'other_unsure',
]

function unique(values: string[]) {
  return [...new Set(values)]
}

export function toScreeningAnswer(value?: TriStateAnswer): 'yes' | 'no' | 'needs_confirmation' {
  if (value === 'yes' || value === 'no') return value
  return 'needs_confirmation'
}

export function operationalStateFromContributor(value: string): 'proposed' | 'experiment' | 'production' | 'retired' {
  if (value === 'testing') return 'experiment'
  if (value === 'in_use') return 'production'
  if (value === 'no_longer_used') return 'retired'
  return 'proposed'
}

export function contributorStateFromOperational(value: string): 'considering' | 'testing' | 'in_use' | 'no_longer_used' {
  if (value === 'experiment' || value === 'pilot') return 'testing'
  if (value === 'production' || value === 'suspended') return 'in_use'
  if (value === 'retired') return 'no_longer_used'
  return 'considering'
}

export function deriveActivityClassifications(observations: ContributorObservations) {
  const values: string[] = []
  for (const activity of activityOrder) {
    if (observations.activities.includes(activity)) values.push(...activityMap[activity])
  }

  if (observations.activities.includes('analyse_predict_recommend')) {
    values.push(observations.analysisPurpose && observations.analysisPurpose !== 'unsure'
      ? observations.analysisPurpose
      : 'other_needs_confirmation')
  }

  if (observations.activities.includes('media_understanding')) {
    if (observations.mediaType === 'images_video') values.push('analyse_images_video')
    else if (observations.mediaType === 'speech_audio') values.push('understand_generate_speech')
    else if (observations.mediaType === 'both') values.push('analyse_images_video', 'understand_generate_speech')
    else values.push('other_needs_confirmation')
  }

  const aiActivity = unique(values)
  return {
    ai_activity: aiActivity,
    primary_ai_activity: aiActivity[0] ?? '',
  }
}

export function deriveAudienceScope(userGroups: string[]) {
  if (userGroups.length === 0 || userGroups.includes('needs_confirmation')) return 'needs_confirmation'
  const hasInternal = userGroups.some((group) => group === 'employees' || group === 'contractors')
  const hasExternal = userGroups.some((group) => externalUserGroups.has(group) || group === 'other')
  if (hasInternal && hasExternal) return 'both'
  if (hasExternal) return 'external'
  if (hasInternal) return 'internal'
  return 'needs_confirmation'
}

export function deriveHumanOversight(value?: HumanCheckObservation) {
  if (value === 'every') return 'review_every_output_action'
  if (value === 'some') return 'review_selected_exceptional'
  if (value === 'none') return 'no_routine_review'
  return 'needs_confirmation'
}

export function deriveDecisionAuthority(observations: ContributorObservations) {
  if (observations.activities.includes('control_physical')) return 'controls_physical_equipment'
  const carriesOutProcess = observations.activities.includes('process_actions')
  if (carriesOutProcess && observations.actionWithoutApproval === 'yes') return 'bounded_actions_without_prior_approval'
  if (carriesOutProcess && observations.actionWithoutApproval === 'unsure') return 'needs_confirmation'
  if (carriesOutProcess && observations.actionWithoutApproval === 'no') return 'action_after_person_approves'
  if (observations.analysisPurpose === 'select_make_decision') return 'makes_materially_determines_decisions'
  if (observations.analysisPurpose === 'recommend_support_decision') return 'recommend_action_decision'
  if (observations.importantDecision === 'yes') return 'recommend_action_decision'
  if (observations.importantDecision === 'unsure') return 'needs_confirmation'
  return 'information_content_only'
}

export function deriveOperatingPattern(observations: ContributorObservations) {
  if (observations.activities.includes('control_physical')) return 'autonomous_physical_system'
  if (observations.activities.includes('process_actions')) return 'automated_workflow'
  const simpleAssistance = observations.activities.length > 0 && observations.activities.every((activity) =>
    ['write_create', 'find_information', 'read_documents', 'analyse_predict_recommend'].includes(activity),
  )
  if (simpleAssistance && observations.userGroups.some((group) => group === 'employees' || group === 'contractors')) return 'assistant_copilot'
  return 'other_needs_confirmation'
}

export function shouldAskActionAuthority(observations: ContributorObservations) {
  return observations.activities.includes('process_actions')
}

export function shouldAskAffectedPeople(observations: ContributorObservations) {
  return observations.activities.some((activity) => ['analyse_predict_recommend', 'process_actions'].includes(activity))
    || observations.activities.includes('control_physical')
    || observations.userGroups.some((group) => externalUserGroups.has(group))
}

export function shouldAskVulnerablePeople(observations: ContributorObservations) {
  return observations.userGroups.some((group) => protectedContextGroups.has(group))
    || (observations.affectedPeople ?? []).some((group) => protectedContextGroups.has(group))
    || observations.importantDecision === 'yes'
    || observations.importantDecision === 'unsure'
}

export function shouldAskContestability(observations: ContributorObservations) {
  const affected = observations.affectedPeople ?? []
  return observations.importantDecision === 'yes'
    || observations.importantDecision === 'unsure'
    || observations.seriousImpact === 'yes'
    || observations.seriousImpact === 'unsure'
    || affected.some((group) => group !== 'no_identifiable_people')
}

export function normaliseContributorObservations(observations: ContributorObservations): ContributorObservations {
  const normalised: ContributorObservations = {
    ...observations,
    activities: unique(observations.activities) as ActivityObservation[],
    userGroups: unique(observations.userGroups),
    affectedPeople: observations.affectedPeople ? unique(observations.affectedPeople) : undefined,
  }

  if (!normalised.activities.includes('media_understanding')) normalised.mediaType = undefined
  if (!normalised.activities.includes('analyse_predict_recommend')) normalised.analysisPurpose = undefined
  if (!shouldAskActionAuthority(normalised)) normalised.actionWithoutApproval = undefined
  if (!shouldAskAffectedPeople(normalised)) normalised.affectedPeople = []
  if (!shouldAskVulnerablePeople(normalised)) normalised.vulnerablePeople = undefined
  if (!shouldAskContestability(normalised)) normalised.contestability = undefined

  return normalised
}

export function deriveClassifications(
  observations: ContributorObservations,
  expectedBenefits: string[] = [],
  inheritedTechnologyFamilies: string[] = [],
) {
  const normalised = normaliseContributorObservations(observations)
  const activities = deriveActivityClassifications(normalised)
  return {
    ...activities,
    operating_pattern: deriveOperatingPattern(normalised),
    decision_authority: deriveDecisionAuthority(normalised),
    human_oversight: deriveHumanOversight(normalised.humanCheck),
    user_group: normalised.userGroups.length > 0 ? normalised.userGroups : ['needs_confirmation'],
    affected_people: normalised.affectedPeople?.length ? normalised.affectedPeople : [],
    audience_scope: deriveAudienceScope(normalised.userGroups),
    technology_family: inheritedTechnologyFamilies.length > 0 ? inheritedTechnologyFamilies : ['needs_technical_confirmation'],
    expected_benefit: expectedBenefits,
  }
}

export function deriveScreening(
  observations: ContributorObservations,
  generalPurposeAnswer: 'yes' | 'no' | 'needs_confirmation' = 'needs_confirmation',
) {
  const normalised = normaliseContributorObservations(observations)
  const answers: Record<string, 'yes' | 'no' | 'needs_confirmation'> = {}
  const provenance: ScreeningProvenance = {}

  answers['GAS-01'] = toScreeningAnswer(normalised.informationHandling)
  provenance['GAS-01'] = {
    source: 'contributor',
    sourceField: 'informationHandling',
    rationale: 'The contributor answered the information-handling question directly.',
  }

  const externalUsers = normalised.userGroups.some((group) => externalUserGroups.has(group))
  if (!externalUsers && !normalised.userGroups.includes('needs_confirmation') && normalised.userGroups.length > 0) {
    answers['GAS-02'] = 'no'
  } else if (externalUsers && normalised.humanCheck === 'none') {
    answers['GAS-02'] = 'yes'
  } else if (externalUsers && normalised.humanCheck === 'every') {
    answers['GAS-02'] = 'no'
  } else {
    answers['GAS-02'] = 'needs_confirmation'
  }
  provenance['GAS-02'] = {
    source: 'derived',
    sourceField: 'userGroups + humanCheck',
    rationale: 'Direct interaction without meaningful oversight is derived from who directly uses the AI and the stated human-check arrangement.',
  }

  const processAction = normalised.activities.includes('process_actions')
  if (!processAction || normalised.actionWithoutApproval === 'no') answers['GAS-03'] = 'no'
  else answers['GAS-03'] = 'needs_confirmation'
  provenance['GAS-03'] = {
    source: 'derived',
    sourceField: 'activities + actionWithoutApproval',
    rationale: processAction && normalised.actionWithoutApproval !== 'no'
      ? 'The contributor indicated system actions may occur without individual approval; scale still requires confirmation before the existing autonomous-action-at-scale rule can be resolved.'
      : 'The contributor did not describe autonomous process actions, so the autonomous-action-at-scale condition does not apply.',
  }

  if (shouldAskVulnerablePeople(normalised)) {
    answers['GAS-04'] = toScreeningAnswer(normalised.vulnerablePeople)
    provenance['GAS-04'] = {
      source: 'contributor',
      sourceField: 'vulnerablePeople',
      rationale: 'Earlier user, affected-person or decision-impact answers made this contributor question relevant.',
    }
  } else {
    answers['GAS-04'] = 'no'
    provenance['GAS-04'] = {
      source: 'derived',
      sourceField: 'userGroups + affectedPeople + importantDecision',
      rationale: 'No earlier response identified a protected/vulnerable-person context requiring this additional question.',
    }
  }

  answers['GAS-05'] = toScreeningAnswer(normalised.regulatedArea)
  provenance['GAS-05'] = {
    source: 'contributor',
    sourceField: 'regulatedArea',
    rationale: 'The contributor answered the regulated/legal/professional-context question directly.',
  }

  if (shouldAskContestability(normalised)) {
    answers['GAS-06'] = toScreeningAnswer(normalised.contestability)
    provenance['GAS-06'] = {
      source: normalised.contestability ? 'contributor' : 'specialist_confirmation',
      sourceField: 'contestability',
      rationale: 'Earlier decision, impact or affected-person answers make correction/contestability relevant.',
    }
  } else {
    answers['GAS-06'] = 'no'
    provenance['GAS-06'] = {
      source: 'derived',
      sourceField: 'importantDecision + seriousImpact + affectedPeople',
      rationale: 'The contributor indicated no important decision, serious impact or identifiable affected-person outcome that would make the existing contestability rule relevant.',
    }
  }

  answers['GAS-07'] = generalPurposeAnswer
  provenance['GAS-07'] = {
    source: generalPurposeAnswer === 'needs_confirmation' ? 'specialist_confirmation' : 'product_inherited',
    sourceField: 'Product/Solution characteristic',
    rationale: generalPurposeAnswer === 'needs_confirmation'
      ? 'General-purpose/adaptable capability is a Product/Solution characteristic and is routed for confirmation rather than guessed by the contributor.'
      : 'The answer is inherited from confirmed Product/Solution information.',
  }

  return { answers, provenance }
}
